import { c as _c } from "react/compiler-runtime";
import { Stack } from "@mui-wrapper";
import { DesktopDateRangePicker } from "@mui/x-date-pickers-pro/DesktopDateRangePicker";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, DatePickerVariant, Text, variantMap } from "@flat-ui";
import { CustomRangeCalendarLayout } from "@components/date-range-picker/date-range-calendar-layout";
import { DateRangeSummaryField } from "@components/date-range-picker/date-range-summary-field";
import { areRangesEqualByDay, buildDefaultPresets, formatShortcutLabel, toDateRange, toDateTime, toDateTimeRange } from "@components/date-range-picker/date-range-picker-utils";

/** The width of the input field */
const INPUT_WIDTH = "12.5rem";

/** Class used to style the active shortcut chip in the calendar popover. */
const ACTIVE_SHORTCUT_CLASS = "DateRangePicker-shortcutActive";

/**
 * A customized MUI X DateRangePicker that follows the FARO design guidelines.
 * NOTE: relies on `@mui/x-date-pickers-pro`, which requires a commercial MUI X
 *
 * @returns a customized MUI DateRangePicker
 */
export function DateRangePicker(t0) {
  const $ = _c(84);
  const {
    label,
    pickerTitle: t1,
    value: sourceValue,
    defaultValue: sourceDefaultValue,
    minDate: sourceMinDate,
    maxDate: sourceMaxDate,
    presets,
    quickPresets,
    shouldEnableCompare: t2,
    compareLabel: t3,
    isCompareChecked,
    onCompareChange,
    variant: t4,
    isReadOnly: t5,
    isDisabled: t6,
    onChange,
    onAccept
  } = t0;
  const pickerTitle = t1 === undefined ? "Select date range" : t1;
  const shouldEnableCompare = t2 === undefined ? false : t2;
  const compareLabel = t3 === undefined ? "Compare to previous timeframe" : t3;
  const variant = t4 === undefined ? DatePickerVariant.Light : t4;
  const isReadOnly = t5 === undefined ? false : t5;
  const isDisabled = t6 === undefined ? false : t6;
  const [isOpen, updateIsOpen] = useState(false);
  const variantStyle = variantMap[variant];
  const isControlled = sourceValue !== undefined;
  let t7;
  if ($[0] !== sourceDefaultValue || $[1] !== sourceValue) {
    t7 = () => toDateTimeRange(sourceValue ?? sourceDefaultValue);
    $[0] = sourceDefaultValue;
    $[1] = sourceValue;
    $[2] = t7;
  } else {
    t7 = $[2];
  }
  const [internalValue, setInternalValue] = useState(t7);
  const pickerValue = internalValue;
  let t8;
  let t9;
  if ($[3] !== isControlled || $[4] !== sourceValue) {
    t8 = () => {
      if (!isControlled) {
        return;
      }
      setInternalValue(toDateTimeRange(sourceValue));
    };
    t9 = [isControlled, sourceValue];
    $[3] = isControlled;
    $[4] = sourceValue;
    $[5] = t8;
    $[6] = t9;
  } else {
    t8 = $[5];
    t9 = $[6];
  }
  useEffect(t8, t9);
  let t10;
  if ($[7] !== sourceMinDate) {
    t10 = toDateTime(sourceMinDate);
    $[7] = sourceMinDate;
    $[8] = t10;
  } else {
    t10 = $[8];
  }
  const minDate = t10;
  let t11;
  if ($[9] !== sourceMaxDate) {
    t11 = toDateTime(sourceMaxDate);
    $[9] = sourceMaxDate;
    $[10] = t11;
  } else {
    t11 = $[10];
  }
  const maxDate = t11;
  let t12;
  if ($[11] !== onAccept || $[12] !== onChange) {
    t12 = (range, t13) => {
      const shouldAccept = t13 === undefined ? false : t13;
      setInternalValue(range);
      onChange?.(toDateRange(range));
      if (shouldAccept) {
        onAccept?.(toDateRange(range));
      }
    };
    $[11] = onAccept;
    $[12] = onChange;
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  const applyRange = t12;
  let t13;
  if ($[14] !== applyRange) {
    t13 = preset => {
      const [start, end] = preset.getValue();
      applyRange([DateTime.fromJSDate(start), DateTime.fromJSDate(end)], true);
    };
    $[14] = applyRange;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  const applyPreset = t13;
  let t14;
  if ($[16] !== presets) {
    t14 = presets ?? buildDefaultPresets();
    $[16] = presets;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  let t15;
  if ($[18] !== pickerValue || $[19] !== t14) {
    let t16;
    if ($[21] !== pickerValue) {
      t16 = t17 => {
        const {
          label: label_0,
          getValue
        } = t17;
        const [start_0, end_0] = getValue();
        const presetRange = [DateTime.fromJSDate(start_0), DateTime.fromJSDate(end_0)];
        return {
          label: formatShortcutLabel(label_0),
          getValue: () => presetRange,
          className: areRangesEqualByDay(pickerValue, presetRange) ? ACTIVE_SHORTCUT_CLASS : undefined
        };
      };
      $[21] = pickerValue;
      $[22] = t16;
    } else {
      t16 = $[22];
    }
    t15 = t14.map(t16);
    $[18] = pickerValue;
    $[19] = t14;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  const shortcutsItems = t15;
  let t16;
  if ($[23] !== variantStyle) {
    t16 = () => <DateRangeSummaryField variantStyle={variantStyle} />;
    $[23] = variantStyle;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  const SummaryFieldWrapper = t16;
  let t17;
  if ($[25] !== compareLabel || $[26] !== isCompareChecked || $[27] !== onCompareChange || $[28] !== pickerTitle || $[29] !== shouldEnableCompare || $[30] !== variant || $[31] !== variantStyle) {
    t17 = props => <CustomRangeCalendarLayout variantStyle={variantStyle} isDark={variant === DatePickerVariant.Dark} pickerTitle={pickerTitle} shouldEnableCompare={shouldEnableCompare} compareLabel={compareLabel} isCompareChecked={isCompareChecked} onCompareChange={onCompareChange} {...props} />;
    $[25] = compareLabel;
    $[26] = isCompareChecked;
    $[27] = onCompareChange;
    $[28] = pickerTitle;
    $[29] = shouldEnableCompare;
    $[30] = variant;
    $[31] = variantStyle;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  const CustomLayoutWrapper = t17;
  let t18;
  if ($[33] !== label) {
    t18 = label && <Text variant="heading12">{label}</Text>;
    $[33] = label;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = {
      alignItems: "center",
      gap: 1,
      flexWrap: "wrap"
    };
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  let t21;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = () => updateIsOpen(true);
    t21 = () => updateIsOpen(false);
    $[36] = t20;
    $[37] = t21;
  } else {
    t20 = $[36];
    t21 = $[37];
  }
  const t22 = minDate ?? undefined;
  const t23 = maxDate ?? undefined;
  let t24;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = {
      cursor: "pointer"
    };
    $[38] = t24;
  } else {
    t24 = $[38];
  }
  let t25;
  if ($[39] !== variantStyle.secondaryColor) {
    t25 = {
      color: variantStyle.secondaryColor,
      fontSize: "1.125rem"
    };
    $[39] = variantStyle.secondaryColor;
    $[40] = t25;
  } else {
    t25 = $[40];
  }
  let t26;
  if ($[41] !== t25 || $[42] !== variantStyle.backgroundColor || $[43] !== variantStyle.inputFieldHoverColor) {
    t26 = {
      "& .MuiInputBase-root": {
        height: "2.375rem",
        width: INPUT_WIDTH,
        minWidth: INPUT_WIDTH,
        maxWidth: INPUT_WIDTH,
        backgroundColor: variantStyle.backgroundColor,
        color: variantStyle.inputFieldHoverColor,
        cursor: "pointer",
        "& .MuiInputBase-input": t24,
        "& .MuiInputAdornment-root .MuiSvgIcon-root": t25
      }
    };
    $[41] = t25;
    $[42] = variantStyle.backgroundColor;
    $[43] = variantStyle.inputFieldHoverColor;
    $[44] = t26;
  } else {
    t26 = $[44];
  }
  let t27;
  if ($[45] !== CustomLayoutWrapper || $[46] !== SummaryFieldWrapper) {
    t27 = {
      field: SummaryFieldWrapper,
      layout: CustomLayoutWrapper
    };
    $[45] = CustomLayoutWrapper;
    $[46] = SummaryFieldWrapper;
    $[47] = t27;
  } else {
    t27 = $[47];
  }
  let t28;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = {
      name: "flip",
      enabled: false
    };
    $[48] = t28;
  } else {
    t28 = $[48];
  }
  let t29;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = {
      placement: "bottom-start",
      modifiers: [t28, {
        name: "preventOverflow",
        options: {
          altAxis: false,
          padding: 8
        }
      }]
    };
    $[49] = t29;
  } else {
    t29 = $[49];
  }
  let t30;
  if ($[50] !== shortcutsItems) {
    t30 = {
      items: shortcutsItems,
      changeImportance: "set"
    };
    $[50] = shortcutsItems;
    $[51] = t30;
  } else {
    t30 = $[51];
  }
  let t31;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = {
      actions: ["cancel", "accept"]
    };
    $[52] = t31;
  } else {
    t31 = $[52];
  }
  let t32;
  if ($[53] !== t30) {
    t32 = {
      popper: t29,
      shortcuts: t30,
      actionBar: t31
    };
    $[53] = t30;
    $[54] = t32;
  } else {
    t32 = $[54];
  }
  let t33;
  let t34;
  if ($[55] !== applyRange) {
    t33 = range_0 => applyRange(range_0);
    t34 = range_1 => {
      updateIsOpen(false);
      const completeRange = [range_1[0], range_1[1] ?? range_1[0]];
      applyRange(completeRange, true);
    };
    $[55] = applyRange;
    $[56] = t33;
    $[57] = t34;
  } else {
    t33 = $[56];
    t34 = $[57];
  }
  let t35;
  if ($[58] !== isDisabled || $[59] !== isOpen || $[60] !== isReadOnly || $[61] !== pickerValue || $[62] !== t22 || $[63] !== t23 || $[64] !== t26 || $[65] !== t27 || $[66] !== t32 || $[67] !== t33 || $[68] !== t34) {
    t35 = <DesktopDateRangePicker open={isOpen} disabled={isDisabled} readOnly={isReadOnly} onOpen={t20} onClose={t21} closeOnSelect={false} value={pickerValue} minDate={t22} maxDate={t23} calendars={1} showDaysOutsideCurrentMonth={true} reduceAnimations={true} disableAutoMonthSwitching={true} sx={t26} slots={t27} slotProps={t32} onChange={t33} onAccept={t34} />;
    $[58] = isDisabled;
    $[59] = isOpen;
    $[60] = isReadOnly;
    $[61] = pickerValue;
    $[62] = t22;
    $[63] = t23;
    $[64] = t26;
    $[65] = t27;
    $[66] = t32;
    $[67] = t33;
    $[68] = t34;
    $[69] = t35;
  } else {
    t35 = $[69];
  }
  let t36;
  if ($[70] !== applyPreset || $[71] !== isDisabled || $[72] !== isReadOnly || $[73] !== quickPresets || $[74] !== variantStyle.inputFieldDefaultBorderColor || $[75] !== variantStyle.secondaryColor || $[76] !== variantStyle.selectedColor) {
    t36 = quickPresets?.map(preset_0 => <Button key={preset_0.label} variant="secondary" disabled={isDisabled || isReadOnly} onClick={() => applyPreset(preset_0)} sx={{
      height: "2.375rem",
      whiteSpace: "nowrap",
      color: variantStyle.secondaryColor,
      borderColor: variantStyle.inputFieldDefaultBorderColor,
      "&:hover": {
        borderColor: variantStyle.selectedColor,
        color: variantStyle.selectedColor,
        backgroundColor: "transparent"
      }
    }}>{preset_0.label}</Button>);
    $[70] = applyPreset;
    $[71] = isDisabled;
    $[72] = isReadOnly;
    $[73] = quickPresets;
    $[74] = variantStyle.inputFieldDefaultBorderColor;
    $[75] = variantStyle.secondaryColor;
    $[76] = variantStyle.selectedColor;
    $[77] = t36;
  } else {
    t36 = $[77];
  }
  let t37;
  if ($[78] !== t35 || $[79] !== t36) {
    t37 = <Stack direction="row" sx={t19}>{t35}{t36}</Stack>;
    $[78] = t35;
    $[79] = t36;
    $[80] = t37;
  } else {
    t37 = $[80];
  }
  let t38;
  if ($[81] !== t18 || $[82] !== t37) {
    t38 = <>{t18}{t37}</>;
    $[81] = t18;
    $[82] = t37;
    $[83] = t38;
  } else {
    t38 = $[83];
  }
  return t38;
}